import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import {
  Button,
  Card,
  Col,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Table, Drawer, DatePicker, Badge, Spin, Input } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";

import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Icon } from "../../components/Component";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import ReportBreakDown from "./ReportBreakDown";
import { updateMenuData } from "../../store/slices/misc/menuData";
import TopMenu from "../../route/topMenu";
import FormSelect from "../form-components/SelectComponent";
import FormMultiSelect from "../form-components/MultiSelectComponent";
import { expensesApis } from "../../api/Expenses";
import { getDirtyFieldsOnly } from "../../utils/use-form/GetDirtyFieldsOnly";
import SkeletonTable from "../../utils/skeleton-templates/table";
import type { SkeletonTableColumnsType } from "../../utils/skeleton-templates/table";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type ReportType =
  | "expense-details"
  | "expenses-by-user"
  | "expenses-by-department"
  | "expenses-by-merchant"
  | "expenses-by-category"
  | "expenses-by-project"
  | "unsubmitted-expenses";

interface ExpenseData {
  id: string;
  expenseReportId?: string | number; // Add the correct field name from API
  report?: string;
  receipt?: string;
  paymentMode?: string;
  category?: string;
  expenseDate?: string;
  expenseAmount?: number;
  expenseAmountFCY?: number;
  user?: string;
  department?: string;
  merchant?: string;
  project?: string;
  description?: string;
  status?: string;
  expenseCount?: number;
  expenseAmountWithoutTax?: number;
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
  }[];
}

const { RangePicker } = DatePicker;

const ExpenseReports = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [searchText, setSearchText] = useState("");
  const [projectId, setProjectId] = useState<string | number | null>(null);

  // Expense reports data
  const expenseReports = [
    {
      key: "1",
      reportName: "Expense Details",
      reportType: "Detailed view of all expense transactions and their status.",
      createdBy: "System Generated",
      onClickNavigation: "/pms/expenses/expense-details-report",
    },
    {
      key: "2",
      reportName: "Expense User",
      reportType: "Expense analysis grouped by users and their spending patterns.",
      createdBy: "System Generated",
      onClickNavigation: "/pms/expenses/expense-user-report",
    },
    {
      key: "3",
      reportName: "Expense Vendor",
      reportType: "Vendor-wise expense breakdown and payment analysis.",
      createdBy: "System Generated",
      onClickNavigation: "/pms/expenses/expense-vendor-report",
    },
    {
      key: "4",
      reportName: "Expense Category",
      reportType: "Category-wise expense distribution and trend analysis.",
      createdBy: "System Generated",
      onClickNavigation: "/pms/expenses/expense-category-report",
    },
  ];

  // Filter reports based on search text
  const filteredReports = expenseReports.filter((report) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase().trim();
    return (
      report.reportName.toLowerCase().includes(searchLower) ||
      report.reportType.toLowerCase().includes(searchLower)
    );
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth >= 990) {
      setSm(false);
    }
  };

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, dispatch]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    sm
      ? document.body.classList.add("toggle-shown")
      : document.body.classList.remove("toggle-shown");
  }, [sm]);

  // Extract projectId from URL params first, then location state
  useEffect(() => {
    const { id: projectIdFromState, projectId: projectIdAlt } = location.state || {};
    const resolvedProjectId = projectIdFromUrl || projectIdFromState || projectIdAlt;
    if (resolvedProjectId && !isNaN(Number(resolvedProjectId))) {
      setProjectId(resolvedProjectId);
    }
  }, [location.state, projectIdFromUrl]);


  return (
    <>
      <Head title="Expense Reports" />
      <Content>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
            <div className="card-inner card-inner-lg">
              <h5 style={{ marginBottom: "20px", fontWeight: "600" }}>
                {location.state?.projectName || "Project Reports"} - Expense Reports
              </h5>
              {/* Search Input */}
              <div style={{ marginBottom: 16 }}>
                <Input
                  placeholder="Search expense reports by name or description..."
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 400, maxWidth: "100%" }}
                />
              </div>

              <div className="content clearfix">
                <Table
                  className="customTable"
                  columns={[
                    {
                      title: "#",
                      key: "key",
                      width: 50,
                      render: (text, record, index) => index + 1,
                    },
                    {
                      title: "Report Name",
                      dataIndex: "reportName",
                      key: "reportName",
                      render: (text, record: any) => (
                        <span
                          className="inlineTableCellLink"
                          onClick={() => {
                            const basePath = record?.onClickNavigation ?? '';
                            const path = projectId != null && !isNaN(Number(projectId))
                              ? `${basePath}/${projectId}`
                              : basePath;
                            navigate(path, {
                              state: {
                                id: projectId,
                                projectId: projectId,
                                projectName: location.state?.projectName,
                              },
                            });
                          }}
                        >
                          {text}
                        </span>
                      ),
                    },
                    {
                      title: "Report Description",
                      dataIndex: "reportType",
                      key: "reportType",
                    },
                  ]}
                  dataSource={filteredReports}
                  locale={{
                    emptyText: (
                      <span
                        style={{
                          height: "300px",
                          color: "#333",
                          fontSize: "16px",
                        }}
                      >
                        {searchText ? (
                          "No expense reports found matching your search."
                        ) : (
                          <>
                            Kindly contact the Admin to request access
                            permission for the <b>{"Expense Reports"}</b> module.
                          </>
                        )}
                      </span>
                    ),
                  }}
                  bordered
                  size="middle"
                  pagination={false}
                />
              </div>
            </div>
        </Card>
      </Content>
    </>
  );
};

export default ExpenseReports;
